import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useHead } from '@unhead/react'
import { useEffect } from 'react'
import { useClientConfig } from '@/entities/client-config/lib'
import { useDashboardLayout, useStateConfigDiagnostics } from '@/entities/state/lib'
import { stateConfigOptions, useStateConfigPoller } from '@/entities/state/model'
import { fetchStoreDetails, storeKeys } from '@/entities/store/api'
import { useOrderSounds } from '@/features/alerts/lib'
import { requireAuth } from '@/features/auth/lib'
import { useAuthStore } from '@/features/auth/model'
import { DriverJsProvider } from '@/features/onboarding/ui'
import { OrderDetailsModalProvider } from '@/features/order/ui/order-details/modal'
import { RealtimeProvider } from '@/features/realtime/lib'
import { useSettingsStore } from '@/shared/model/settings.store'
import { DashboardConfigurationErrorScreen, DashboardLoadingScreen, DashboardShell } from '@/widgets/dashboard-layout'

export const Route = createFileRoute('/_dashboard')({
  component: RouteComponent,
  beforeLoad: () => requireAuth(),
  loader: async ({ context: { queryClient } }) => {
    // Check if user is authenticated
    const authState = useAuthStore.getState()
    if (!authState.isAuthenticated()) {
      return
    }

    await Promise.all([
      queryClient.prefetchQuery(stateConfigOptions()),
      queryClient.prefetchQuery({
        queryKey: storeKeys.details(),
        queryFn: fetchStoreDetails,
      }),
    ])
  },
})

function RouteComponent() {
  const { config } = useClientConfig()
  const { layout, isPending, isError } = useDashboardLayout()
  const setAllowSound = useSettingsStore(state => state.setAllowSound)

  useStateConfigPoller()
  useOrderSounds()

  useHead({
    style: [{
      innerHTML: `
        :root {
          --hs-theme-background-color-gradient-one: ${config.theme.backgroundColorOne};
          --hs-theme-background-color-gradient-two: ${config.theme.backgroundColorTwo};
          --hs-theme-header-text-color: ${config.theme.headerTextColor ?? '#ffffff'};
          --hs-logo-filter: ${config.theme.headerTextColor ? 'brightness(0)' : 'brightness(0) invert(1)'};
        }
      `,
    }],
  })

  useStateConfigDiagnostics()

  // The dashboard is only "ready" once a valid layout is available and the gate
  // below will render the real content. Sound protection must not start counting
  // down while the loading/error screen is showing.
  const isReady = !isPending && !isError && Boolean(layout)

  // Initialize sound protection - allow sound after 2 seconds of dashboard mount
  useEffect(() => {
    if (!isReady) {
      return
    }

    const timer = setTimeout(() => {
      setAllowSound(true)
    }, 2000)

    return () => clearTimeout(timer)
  }, [isReady, setAllowSound])

  // Hard gate: the dashboard layout comes from the state config
  // (GET /configuration/states). Children must not mount until a valid layout
  // is available. Missing layout is a blocking configuration error, never a
  // silent fallback.
  if (isPending) {
    return <DashboardLoadingScreen />
  }

  if (isError || !layout) {
    return <DashboardConfigurationErrorScreen />
  }

  return (
    <DriverJsProvider>
      <RealtimeProvider>
        <DashboardShell>
          <Outlet />
          <OrderDetailsModalProvider />
        </DashboardShell>
      </RealtimeProvider>
    </DriverJsProvider>
  )
}
